/**
 * Viewing rooms from the owner's side: the list of rooms a collector has
 * published, the form that makes or edits one, and the handle that gives them
 * an address at all.
 *
 * The visitor's side is `room/`, which reads a room with no session; this
 * file never sees a visitor except as a row in the log. The fetching and the
 * stage live in `useViewingRoomsController.ts`, the words and caps in
 * `viewingRooms.constants.ts`, and the paged search behind the works picker in
 * `workPicker.model.ts`.
 *
 * Two rules here exist only to be tested. `withSourceKind` clears what was
 * ticked when the kind changes, because a set of collection ids and a set of
 * work ids are the same type and nothing else stands between them. And an
 * empty password on EDIT means "leave it" where on create it means "none" —
 * a form cannot show a password back, and renaming a room must not unlock it.
 */

import {
  HANDLE_PATTERN,
  isOwner,
  normaliseHandle,
  roomPath,
} from "@ondo/shared";
import type {
  CollectionSummary,
  CreateViewingRoomRequest,
  RoomVisitor,
  UpdateViewingRoomRequest,
  ViewingRoomSummary,
  ViewingRoomWork,
} from "@ondo/shared";

import { describeApiError } from "../lib/apiError.ts";
import { labelFor } from "./workPicker.model.ts";
import type { Choice } from "../lib/choices.ts";
import {
  MAX_CHOSEN,
  PASSWORD_MIN,
  VIEWING_ROOMS_COPY,
} from "./viewingRooms.constants.ts";
import type { SourceKind } from "./viewingRooms.constants.ts";

/** What the create form holds. Nothing here has been sent anywhere. */
export interface RoomDraft {
  title: string;
  subtitle: string;
  message: string;
  sourceKind: SourceKind;
  /**
   * What has been ticked — collections OR works, by kind. One list rather than
   * two, because only one kind is ever live and two would let a stale selection
   * from the other ride along into the request. `withSourceKind` is what keeps
   * that true, and it is the only thing that does.
   *
   * `Choice`, not a bare id, because the works picker is paged: a work chosen
   * on page one is not in the result set by page four, and the running list of
   * what you have chosen still has to be able to say its name. Carrying the
   * label with the key is what makes that possible without a second lookup —
   * and `lib/choices.ts` argues the case for one shared shape rather than a
   * `{ id, label }` here and a `{ key, label }` there.
   */
  chosen: Choice[];
  showProvenance: boolean;
  showPresentOwner: boolean;
  showDimensions: boolean;
  /**
   * A new password, or "".
   *
   * "" means two different things depending on the mode, which is why
   * `clearPassword` exists beside it: when creating, "" is a room with no
   * password; when editing, "" is "leave whatever is there alone", because a
   * form cannot show a password back and blanking a field the user never
   * touched must not unlock the room.
   */
  password: string;
  /** Edit only. The explicit act that removes a password, as opposed to not typing one. */
  clearPassword: boolean;
}

export const EMPTY_DRAFT: RoomDraft = {
  title: "",
  subtitle: "",
  message: "",
  sourceKind: "all",
  chosen: [],
  showProvenance: false,
  showPresentOwner: false,
  showDimensions: false,
  password: "",
  clearPassword: false,
};

/**
 * Which act the form is performing.
 *
 * A union rather than a boolean beside a nullable id: "editing, but no room"
 * and "creating, but a room id" are both unrepresentable, and every branch that
 * needs the id has it.
 */
export type FormMode = { kind: "create" } | { kind: "edit"; roomId: string };

/**
 * The form, filled in from a room that already exists.
 *
 * `collectionIds` comes off the summary; the works do not, and are loaded
 * separately — see `useViewingRoomsController.startEditing`. Labels matter for
 * the same reason they do in the picker: a chip has to be able to say its name
 * without the thing it names being on screen.
 */
export function draftFromRoom(
  room: ViewingRoomSummary,
  selection: { collections: Choice[]; works: Choice[] },
): RoomDraft {
  const sourceKind: SourceKind =
    room.scope === "all_collections" ? "all" : room.scope === "collections" ? "collections" : "works";

  return {
    title: room.title,
    subtitle: room.subtitle ?? "",
    message: room.message ?? "",
    sourceKind,
    chosen:
      sourceKind === "collections"
        ? selection.collections
        : sourceKind === "works"
          ? selection.works
          : [],
    showProvenance: room.showProvenance,
    showPresentOwner: room.showPresentOwner,
    showDimensions: room.showDimensions,
    // Never prefilled. A form cannot show a password back, and a field that
    // looked filled would invite somebody to "keep" a value they cannot see.
    password: "",
    clearPassword: false,
  };
}

export interface PreviewItem {
  id: string;
  /** 'Fat Car — Erwin Wurm, 2001'. */
  label: string;
  imageUrl: string | null;
}

/**
 * A few of a room's works, and how many were left out.
 *
 * Null while the answer has not arrived; a `PreviewView` with no items is a
 * real answer — a room whose named works have all left the collections they
 * were in publishes nothing, and saying so is more use than a blank.
 */
export interface PreviewView {
  items: PreviewItem[];
  /** "and 56 more", or null when the preview is the whole room. */
  more: string | null;
  /** "60 works", already pluralised. */
  count: string;
}

/**
 * The first few works of a room, for a glance rather than a list.
 *
 * Takes what the server said the room holds — resolved through
 * `ondo.room_work_ids()`, so it is what a visitor would actually be shown
 * rather than what the room once named.
 */
export function toPreview(
  works: ViewingRoomWork[],
  images: Map<string, string>,
  limit: number,
): PreviewView {
  return {
    items: works.slice(0, limit).map((work) => ({
      id: work.id,
      label: labelFor(work),
      // Absent rather than broken: `signWorkThumbnails` simply omits a path
      // storage refused, and WorkThumb draws its own placeholder.
      imageUrl: images.get(work.id) ?? null,
    })),
    more: works.length > limit ? `and ${String(works.length - limit)} more` : null,
    count: `${String(works.length)} ${works.length === 1 ? "work" : "works"}`,
  };
}

/** One room in the list, with everything already decided. */
export interface RoomRow {
  id: string;
  title: string;
  /** '/collector/me/viewing-rooms/<id>' */
  href: string;
  /** 'https://ondo.app/room/hue-williams/22c0…' — absolute, ready to paste. */
  url: string;
  hidden: boolean;
  locked: boolean;
  /** "12 works · Password set · 4 visits", assembled. */
  meta: string;
  /** What the room discloses, as a sentence. */
  shows: string;
  /** False while hidden: a link that answers nothing is not one to hand out. */
  canCopy: boolean;
}

export interface VisitRow {
  id: string;
  /** The address they typed, or the name the registry knows them by. */
  who: string;
  /** True when the address came from an ONDO account rather than a text box. */
  member: boolean;
  /** "3 opens" — absent for a single one. */
  opens: string | null;
  when: string;
}

/** The pickable things, for whichever kind the draft is on. */
export interface SourceView {
  kind: SourceKind;
  /** Empty while loading, or when there is nothing the caller may publish. */
  options: Choice[];
  chosen: Choice[];
  loading: boolean;
  /** Set when there is nothing to offer at all, rather than nothing yet. */
  nothing: string | null;
}

/**
 * Switches what a room is built over, and clears what was ticked.
 *
 * The clearing is the whole point, and it used to be one line in the controller
 * with a comment and no test: a set of collection ids must never be sent as a
 * set of work ids. The types cannot tell the two apart — both are `Choice[]` —
 * so this function is the only thing standing between them, which is why it
 * lives in the model where it can be tested.
 */
export function withSourceKind(draft: RoomDraft, kind: SourceKind): RoomDraft {
  return { ...draft, sourceKind: kind, chosen: [] };
}

/**
 * Whether a key is ticked.
 *
 * One spelling of the comparison. It was `chosen.includes(key)` in two places,
 * which is how one of them ends up wrong — and under `Choice[]` that expression
 * still compiles, silently returning false forever.
 */
export function isChosen(chosen: Choice[], key: string): boolean {
  return chosen.some((choice) => choice.key === key);
}

/** Ticks or unticks, keeping the label so a paged-away choice can still be named. */
export function toggleChoice(chosen: Choice[], choice: Choice): Choice[] {
  return isChosen(chosen, choice.key)
    ? chosen.filter((each) => each.key !== choice.key)
    : [...chosen, choice];
}

export type RoomsView =
  | { kind: "loading"; message: string }
  | { kind: "error"; message: string }
  /**
   * No handle yet, so there is no address a link could point at.
   *
   * A real gate rather than a banner: every other state on this screen is about
   * a URL, and there is not one to be about.
   */
  | {
      kind: "needs-handle";
      lead: string;
      detail: string;
      label: string;
      value: string;
      /** 'https://ondo.app/room/hue-williams/…', shown as they type. */
      preview: string | null;
      problem: string | null;
      busy: boolean;
    }
  | { kind: "empty"; lead: string; detail: string; createLabel: string }
  | {
      kind: "list";
      rows: RoomRow[];
      createLabel: string;
      busy: boolean;
      error: string | null;
      copiedId: string | null;
      confirmingId: string | null;
    }
  /**
   * One form for both acts.
   *
   * Not two variants: making a room and changing one ask exactly the same
   * questions, and the only differences are the words on the heading and the
   * button. Two variants would be the same twenty fields twice, free to drift.
   */
  | {
      kind: "form";
      mode: FormMode;
      /** "New viewing room" / "Edit this room". */
      heading: string;
      submitLabel: string;
      busyLabel: string;
      /** Said only when editing: the link is already out there. */
      liveNote: string | null;
      draft: RoomDraft;
      source: SourceView;
      /** What this form found wrong before sending anything. */
      problem: string | null;
      /**
       * What the server said when it refused.
       *
       * Separate from `problem` because they fail differently — one is a rule
       * this screen knows and the other is one only the registry does — and
       * carried on this variant because the alternative is what it replaced: an
       * API refusal that landed on a state the create form never renders, so a
       * room silently failed to open with nothing on screen to say why.
       */
      error: string | null;
      busy: boolean;
      /** Whether the work picker is open over this form. */
      picking: boolean;
      /**
       * What the room holds right now. Null while it is coming, and always null
       * when creating — there is no room to ask about until it exists.
       */
      preview: PreviewView | null;
    }
  /** The link, once. A room is only new the moment it is made. */
  | { kind: "created"; row: RoomRow; lead: string; note: string }
  | {
      kind: "detail";
      row: RoomRow;
      /** Null while the visit list is still arriving. */
      visits: VisitRow[] | null;
      backHref: string;
      busy: boolean;
      error: string | null;
      copied: boolean;
      confirming: boolean;
      preview: PreviewView | null;
    };

/**
 * What the controller is in the middle of, when it is not simply listing.
 *
 * `picking` hangs off `creating` rather than being a boolean of its own,
 * because you cannot be choosing works for a room you are not creating — and a
 * union is how that combination stops being representable.
 */
export type Stage =
  | { kind: "browsing" }
  | { kind: "form"; mode: FormMode; draft: RoomDraft; picking: boolean }
  | { kind: "created"; roomId: string };

/**
 * The screen, from everything the controller holds.
 *
 * Order matters and is asserted: a room id in the URL beats a half-typed create
 * form, because the address bar must not claim a screen that is not on it.
 */
export function resolveRooms(input: {
  handle: string | null;
  handleDraft: string;
  handleProblem: string | null;
  rooms: ViewingRoomSummary[] | null;
  failure: string | null;
  record: string | null;
  stage: Stage;
  source: SourceView;
  problem: string | null;
  visits: RoomVisitor[] | null;
  preview: PreviewView | null;
  busy: boolean;
  error: string | null;
  copiedId: string | null;
  confirmingId: string | null;
  origin: string;
  roomsHref: string;
}): RoomsView {
  if (input.failure) return { kind: "error", message: input.failure };

  if (input.handle === null) {
    return {
      kind: "needs-handle",
      lead: VIEWING_ROOMS_COPY.handleLead,
      detail: VIEWING_ROOMS_COPY.handleDetail,
      label: VIEWING_ROOMS_COPY.handleLabel,
      value: input.handleDraft,
      preview: previewFor(input.origin, input.handleDraft),
      problem: input.handleProblem,
      busy: input.busy,
    };
  }

  if (!input.rooms) return { kind: "loading", message: VIEWING_ROOMS_COPY.loading };

  const rows = input.rooms.map((room) =>
    toRoomRow(room, { origin: input.origin, roomsHref: input.roomsHref }),
  );

  const named = input.record === null ? null : rows.find((row) => row.id === input.record);
  const form = input.stage.kind === "form" ? input.stage : null;

  /**
   * Editing the room the URL names beats showing it, and only that.
   *
   * The rule the other way round is still the rule: a half-typed CREATE form
   * must not stay on screen when the address bar names a room, because the
   * address would then be claiming something that is not there. An edit form
   * FOR THAT ROOM is not a different screen — it is the same room, open.
   */
  const editingNamed =
    form !== null && form.mode.kind === "edit" && form.mode.roomId === input.record;

  if (named && !editingNamed) {
    return {
      kind: "detail",
      row: named,
      visits: input.visits === null ? null : input.visits.map(toVisitRow),
      backHref: input.roomsHref,
      busy: input.busy,
      error: input.error,
      copied: input.copiedId === named.id,
      confirming: input.confirmingId === named.id,
      preview: input.preview,
    };
  }

  if (form) {
    const editing = form.mode.kind === "edit";

    return {
      kind: "form",
      mode: form.mode,
      heading: editing ? VIEWING_ROOMS_COPY.editHeading : VIEWING_ROOMS_COPY.createHeading,
      submitLabel: editing ? VIEWING_ROOMS_COPY.editSubmit : VIEWING_ROOMS_COPY.createSubmit,
      busyLabel: editing ? VIEWING_ROOMS_COPY.editBusy : VIEWING_ROOMS_COPY.createBusy,
      liveNote: editing ? VIEWING_ROOMS_COPY.editLiveNote : null,
      draft: form.draft,
      source: input.source,
      problem: input.problem,
      error: input.error,
      busy: input.busy,
      // Only ever open over the works kind. Switching kinds closes it, which is
      // `withSourceKind`'s job — see the controller.
      picking: form.picking && form.draft.sourceKind === "works",
      // Only when a room exists. A create form has nothing to preview: what it
      // will hold is not knowable until the registry resolves it.
      preview: editing ? input.preview : null,
    };
  }

  if (input.stage.kind === "created") {
    const made = rows.find((row) => row.id === (input.stage as { roomId: string }).roomId);
    if (made) {
      return {
        kind: "created",
        row: made,
        lead: VIEWING_ROOMS_COPY.createdLead,
        note: VIEWING_ROOMS_COPY.createdNote,
      };
    }
  }

  if (rows.length === 0) {
    return {
      kind: "empty",
      lead: VIEWING_ROOMS_COPY.emptyLead,
      detail: VIEWING_ROOMS_COPY.emptyDetail,
      createLabel: VIEWING_ROOMS_COPY.createAction,
    };
  }

  return {
    kind: "list",
    rows,
    createLabel: VIEWING_ROOMS_COPY.createAction,
    busy: input.busy,
    error: input.error,
    copiedId: input.copiedId,
    confirmingId: input.confirmingId,
  };
}

/**
 * A room's whole presentation, including its absolute link.
 *
 * `origin` is passed in rather than read off `window`, so this file imports no
 * browser global and the test can assert the entire URL rather than its tail.
 */
export function toRoomRow(
  room: ViewingRoomSummary,
  context: { origin: string; roomsHref: string },
): RoomRow {
  const hidden = room.status !== "open";

  return {
    id: room.id,
    title: room.title,
    href: `${context.roomsHref}/${room.id}`,
    url: `${context.origin}${roomPath(room.handle, room.slug)}`,
    hidden,
    locked: room.hasPassword,
    meta: [
      countOf(room.workCount, "work", "works"),
      room.hasPassword ? VIEWING_ROOMS_COPY.passwordSet : VIEWING_ROOMS_COPY.passwordNone,
      countOf(room.visitorCount, "visit", "visits"),
      ...(hidden ? [VIEWING_ROOMS_COPY.hiddenBadge] : []),
    ].join(" · "),
    shows: describeDisclosure(room),
    // A hidden room's link 404s, so offering it to copy would be offering
    // something that does not work.
    canCopy: !hidden,
  };
}

/**
 * What a room discloses, as a sentence rather than three ticks.
 *
 * Written out because this is the line a collector re-reads before sending a
 * link, and "Provenance: on" does not say what will be on the page.
 */
export function describeDisclosure(room: {
  showProvenance: boolean;
  showPresentOwner: boolean;
  showDimensions: boolean;
}): string {
  const parts: string[] = [];

  if (room.showProvenance) {
    parts.push(
      room.showPresentOwner
        ? "the chain of title including the present owner"
        : "the chain of title up to the present owner",
    );
  }
  if (room.showDimensions) parts.push("dimensions and edition");

  if (parts.length === 0) return "Shows the catalogue entry only.";
  return `Shows the catalogue entry, plus ${parts.join(" and ")}.`;
}

export function toVisitRow(visit: RoomVisitor): VisitRow {
  return {
    id: visit.id,
    // One or the other, never both — the API refuses to send both, and this
    // must not invent a fallback that implies it could.
    who: visit.memberName ?? visit.email ?? "—",
    member: visit.memberName !== null,
    opens: visit.viewCount > 1 ? `${String(visit.viewCount)} opens` : null,
    when: visit.lastSeenAt,
  };
}

/**
 * The collections this caller may publish.
 *
 * `collectionChoices()` is deliberately NOT reused: it includes collections
 * shared WITH you, which is right for a grid of what you can see and wrong for a
 * list of what you may put on the open web. The database refuses those anyway —
 * this is so the form never offers one and then reports a refusal.
 */
export function roomCollectionOptions(collections: CollectionSummary[]): Choice[] {
  return collections
    .filter((collection) => isOwner(collection.level))
    .map((collection) => ({ key: collection.id, label: collection.name }));
}

/** One message, or null. The `validateCollectionName` convention. */
export function validateDraft(draft: RoomDraft): string | null {
  if (draft.title.trim() === "") return VIEWING_ROOMS_COPY.titleRequired;
  if (draft.sourceKind !== "all" && draft.chosen.length === 0) {
    return VIEWING_ROOMS_COPY.sourceEmpty;
  }
  // Caught here rather than at submit. The API's own schema caps it, and until
  // the picker was paged nobody could reach that cap — now somebody will, and
  // finding out after twenty minutes of choosing, from a message that by design
  // names nothing, would be the worst possible moment to learn it.
  if (draft.chosen.length > MAX_CHOSEN) return VIEWING_ROOMS_COPY.tooMany;
  if (draft.password !== "" && draft.password.length < PASSWORD_MIN) {
    return VIEWING_ROOMS_COPY.passwordShort;
  }
  return null;
}

/**
 * The handle, or a reason it is not one.
 *
 * Normalises first and validates the result, which is the order that matters:
 * 'Hue-Williams' is unambiguously a handle and 'my room' is not, and lowercasing
 * must not be allowed to rescue the second.
 */
export function validateHandle(raw: string): string | null {
  return HANDLE_PATTERN.test(normaliseHandle(raw)) ? null : VIEWING_ROOMS_COPY.handleInvalid;
}

/** The request body for a draft the form has already accepted. */
export function toCreateRequest(draft: RoomDraft): CreateViewingRoomRequest {
  const body: CreateViewingRoomRequest = {
    title: draft.title.trim(),
    subtitle: draft.subtitle.trim() === "" ? null : draft.subtitle.trim(),
    message: draft.message.trim() === "" ? null : draft.message.trim(),
    scope: draft.sourceKind === "all" ? "all_collections" : draft.sourceKind,
    showProvenance: draft.showProvenance,
    // The present owner is a facet of the chain. Sending it on for a room that
    // withholds the chain would be rejected by the database's own constraint,
    // so it is resolved here rather than argued about over the wire.
    showPresentOwner: draft.showProvenance && draft.showPresentOwner,
    showDimensions: draft.showDimensions,
    // "" is not a password; null is an open room.
    password: draft.password === "" ? null : draft.password,
  };

  // KEYS, never labels. The two are both strings and the compiler cannot tell
  // them apart, so a careless fix of a type error here would send work titles
  // as work ids — and `create_viewing_room` would refuse them with a message
  // that names nothing. There is a test whose fixture makes that fail loudly.
  const keys = draft.chosen.map((choice) => choice.key);

  if (draft.sourceKind === "collections") return { ...body, collectionIds: keys };
  if (draft.sourceKind === "works") return { ...body, workIds: keys };
  return body;
}

/**
 * The request body for an edit the form has already accepted.
 *
 * Sends every field, because the form showed every field — a wizard is not a
 * patch, and a subtitle the user cleared has to arrive as a clearing rather
 * than as an absence. `""` is what says "remove this"; `ondo.update_viewing_room`
 * reads null as "leave alone" and an empty string as "take it out".
 *
 * The PASSWORD is the exception, and it is the one that matters. A form cannot
 * show a password back, so an untouched field is empty — and if empty meant
 * "no password" the way it does on create, then renaming a room would unlock
 * it. Empty therefore means "leave it", and removing one is a separate,
 * deliberate act.
 */
export function toUpdateRequest(draft: RoomDraft): UpdateViewingRoomRequest {
  const keys = draft.chosen.map((choice) => choice.key);

  const body: UpdateViewingRoomRequest = {
    title: draft.title.trim(),
    subtitle: draft.subtitle.trim(),
    message: draft.message.trim(),
    scope: draft.sourceKind === "all" ? "all_collections" : draft.sourceKind,
    collectionIds: draft.sourceKind === "collections" ? keys : [],
    workIds: draft.sourceKind === "works" ? keys : [],
    showProvenance: draft.showProvenance,
    showPresentOwner: draft.showProvenance && draft.showPresentOwner,
    showDimensions: draft.showDimensions,
  };

  if (draft.clearPassword) return { ...body, clearPassword: true };
  if (draft.password !== "") return { ...body, password: draft.password };
  return body;
}

/** See `describeApiError`; kept under this name for its controllers and tests. */
export function describeError(error: unknown): string {
  return describeApiError(error, VIEWING_ROOMS_COPY.unknownError);
}

/** The link a handle would produce, shown while it is being typed. */
function previewFor(origin: string, draft: string): string | null {
  const handle = normaliseHandle(draft);
  return HANDLE_PATTERN.test(handle) ? `${origin}/room/${handle}/…` : null;
}

function countOf(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}
